import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import AnuncioBanner from '@/components/AnuncioBanner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Target, Trophy, User, Camera, Play, Check, X, Star, Zap, Undo2, Trash2, Pencil, Shield, Users, Plus, ChevronRight, BarChart3 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Confetti from 'react-confetti';

// Sonidos del juego
const SOUNDS = {
  gol: 'https://assets.mixkit.co/active_storage/sfx/2570/2570-preview.mp3',
  fallo: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3',
  bonus: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',
  cambio: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3',
  victoria: 'https://assets.mixkit.co/active_storage/sfx/2020/2020-preview.mp3',
  record: 'https://assets.mixkit.co/active_storage/sfx/1998/1998-preview.mp3',
  silbato: 'https://assets.mixkit.co/active_storage/sfx/952/952-preview.mp3' // Silbato de árbitro
};

const playSound = (type) => {
  try {
    const audio = new Audio(SOUNDS[type]);
    audio.volume = 0.6;
    audio.play().catch(() => { /* Silently ignore */ });
  } catch (e) { /* Silently ignore */ }
};

const CATEGORIAS = {
  benjamin: { nombre: "Benjamín", edad: "8-10 años" },
  alevin: { nombre: "Alevín", edad: "11-12 años" },
  infantil: { nombre: "Infantil", edad: "13-14 años" },
  cadete: { nombre: "Cadete", edad: "15-16 años" },
  juvenil: { nombre: "Juvenil", edad: "17-18 años" },
  senior: { nombre: "Senior", edad: "19+ años" }
};

function CienTiros() {
  const { user } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrearPerfil, setShowCrearPerfil] = useState(false);
  const [showJuego, setShowJuego] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  
  // Estado del juego
  const [partidaActual, setPartidaActual] = useState(null);
  const [faseActual, setFaseActual] = useState('izquierda'); // izquierda, derecha, doble
  const [rondaActual, setRondaActual] = useState(1);
  const [tirosRonda, setTirosRonda] = useState([]);
  const [puntosAcumulados, setPuntosAcumulados] = useState(0);
  const [puedeDeshacer, setPuedeDeshacer] = useState(false);
  const [puntosRondaAnterior, setPuntosRondaAnterior] = useState(null);
  const [estadisticasFinales, setEstadisticasFinales] = useState(null);
  const [showEstadisticas, setShowEstadisticas] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [alertaCambio, setAlertaCambio] = useState(null); // { tipo: 'pie' | 'doble', mensaje: '' }
  
  // Modo competición
  const [modoCompeticion, setModoCompeticion] = useState(false);
  const [showCompeticion, setShowCompeticion] = useState(false);
  const [jugadoresCompeticion, setJugadoresCompeticion] = useState(['', '']);
  const [jugadorActualIndex, setJugadorActualIndex] = useState(0);
  const [puntuacionesCompeticion, setPuntuacionesCompeticion] = useState({});
  const [rondasCompeticion, setRondasCompeticion] = useState({});
  const [showResultadosCompeticion, setShowResultadosCompeticion] = useState(false);
  
  // Admin state
  const [todasPartidas, setTodasPartidas] = useState([]);
  const [showEditarPartida, setShowEditarPartida] = useState(false);
  const [partidaEditando, setPartidaEditando] = useState(null);
  const [nuevaPuntuacion, setNuevaPuntuacion] = useState('');
  const [showMedidas, setShowMedidas] = useState(false);
  const [porteriaSeleccionada, setPorteriaSeleccionada] = useState('pequena');
  const [porteriaFiltro, setPorteriaFiltro] = useState('todas');
  
  const PORTERIAS = {
    pequena: { nombre: "Pequeña (MK)", ancho: "1.80m", alto: "1.20m", distancia: "8 metros" },
    sala: { nombre: "Fútbol Sala", ancho: "3m", alto: "2m", distancia: "Punto de penalty" },
    futbol11: { nombre: "Fútbol 11", ancho: "7.32m", alto: "2.44m", distancia: "Punto de penalty" }
  };
  
  // Formulario crear perfil
  const [nombreJugador, setNombreJugador] = useState('');
  const [categoriaEdad, setCategoriaEdad] = useState('');
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    fetchRanking();
  }, [categoriaFiltro, porteriaFiltro]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/100tiros/perfil`),
        axios.get(`${API}/100tiros/ranking`),
        axios.get(`${API}/100tiros/historial`)
      ];
      
      // Si es entrenador, cargar todas las partidas
      if (user?.role === 'entrenador') {
        requests.push(axios.get(`${API}/100tiros/admin/partidas`));
      }
      
      const responses = await Promise.all(requests);
      setPerfil(responses[0].data);
      setRanking(responses[1].data);
      setHistorial(responses[2].data);
      
      if (user?.role === 'entrenador' && responses[3]) {
        setTodasPartidas(responses[3].data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRanking = async () => {
    try {
      let url = `${API}/100tiros/ranking`;
      const params = [];
      if (categoriaFiltro !== 'todas') params.push(`categoria=${categoriaFiltro}`);
      if (porteriaFiltro !== 'todas') params.push(`tipo_porteria=${porteriaFiltro}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const res = await axios.get(url);
      setRanking(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const crearPerfil = async () => {
    try {
      let fotoUrl = null;
      
      if (fotoFile) {
        const formData = new FormData();
        formData.append('foto', fotoFile);
        const fotoRes = await axios.post(`${API}/100tiros/perfil/foto`, formData);
        fotoUrl = fotoRes.data.foto_url;
      }
      
      await axios.post(`${API}/100tiros/perfil`, {
        nombre_jugador: nombreJugador,
        categoria_edad: categoriaEdad,
        foto_jugador: fotoUrl
      });
      
      setShowCrearPerfil(false);
      fetchData();
    } catch (error) {
      alert('Error al crear perfil: ' + (error.response?.data?.detail || error.message));
    }
  };

  const iniciarPartida = async (esCompeticion = false) => {
    try {
      // Solo resetear competición si NO es modo competición
      if (!esCompeticion) {
        setModoCompeticion(false);
        setPuntuacionesCompeticion({});
        setRondasCompeticion({});
        setJugadorActualIndex(0);
      }
      
      const res = await axios.post(`${API}/100tiros/partida?tipo_porteria=${porteriaSeleccionada}`);
      setPartidaActual(res.data.id);
      setFaseActual('izquierda');
      setRondaActual(1);
      setTirosRonda([]);
      setPuntosAcumulados(0);
      setPuedeDeshacer(false);
      setPuntosRondaAnterior(null);
      setAlertaCambio(null);
      setShowMedidas(false);
      setShowJuego(true);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const mostrarMedidasYJugar = () => {
    // Si venimos de competición, resetear primero
    setModoCompeticion(false);
    setShowMedidas(true);
  };

  const abandonarPartida = () => {
    setShowJuego(false);
    setPartidaActual(null);
    setTirosRonda([]);
    setPuntosAcumulados(0);
    setFaseActual('izquierda');
    setRondaActual(1);
    setPuntosRondaAnterior(null);
    setModoCompeticion(false); // Resetear modo
    setAlertaCambio(null);
  };

  const registrarTiro = (puntos) => {
    // Si es modo competición, usar función específica
    if (modoCompeticion) {
      registrarTiroCompeticion(puntos);
      return;
    }
    
    const nuevosTiros = [...tirosRonda, puntos];
    setTirosRonda(nuevosTiros);
    setPuedeDeshacer(true);
    
    if (nuevosTiros.length === 7) {
      // Ronda completa, enviar al servidor
      guardarRonda(nuevosTiros);
    }
  };

  const deshacerUltimoTiro = () => {
    if (tirosRonda.length > 0) {
      // Si hay tiros en la ronda actual, solo quitar el último del array local
      setTirosRonda(tirosRonda.slice(0, -1));
      if (tirosRonda.length === 1) {
        setPuedeDeshacer(false);
      }
    }
  };

  // Funciones de administración (solo entrenador)
  const eliminarPartidaAdmin = async (partidaId) => {
    if (!window.confirm('¿Eliminar esta partida?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/100tiros/admin/partida/${partidaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('Partida eliminada');
        fetchData();
      } else {
        const data = await response.json();
        alert('Error: ' + (data.detail || 'No se pudo eliminar'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    }
  };

  const abrirEditarPartida = (partida) => {
    setPartidaEditando(partida);
    setNuevaPuntuacion(partida.puntuacion_total.toString());
    setShowEditarPartida(true);
  };

  const guardarEdicionPartida = async () => {
    try {
      const puntos = parseInt(nuevaPuntuacion);
      if (isNaN(puntos) || puntos < 0 || puntos > 1000) {
        alert('La puntuación debe ser un número entre 0 y 1000');
        return;
      }
      
      await axios.put(`${API}/100tiros/admin/partida/${partidaEditando.id}?puntuacion_total=${puntos}`);
      setShowEditarPartida(false);
      setPartidaEditando(null);
      fetchData();
      alert('Partida actualizada correctamente');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Función para modo competición - guardar ronda y cambiar jugador
  const guardarRondaCompeticion = (tiros) => {
    const jugadoresValidos = jugadoresCompeticion.filter(n => n.trim());
    const jugadorActual = jugadoresValidos[jugadorActualIndex];
    const puntosRonda = tiros.reduce((acc, t) => acc + t, 0);
    
    // Bonus si mete los 7
    const todosGoles = tiros.every(t => t > 0);
    const puntosFinal = todosGoles ? puntosRonda * 2 : puntosRonda;
    
    // Pitido al acabar la ronda de 7 tiros
    playSound('silbato');
    
    // Actualizar puntuaciones
    setPuntuacionesCompeticion(prev => ({
      ...prev,
      [jugadorActual]: (prev[jugadorActual] || 0) + puntosFinal
    }));
    
    // Guardar detalle de la ronda
    setRondasCompeticion(prev => ({
      ...prev,
      [jugadorActual]: [...(prev[jugadorActual] || []), { 
        ronda: rondaActual, 
        puntos: puntosFinal, 
        bonus: todosGoles,
        tiros: tiros
      }]
    }));
    
    setTirosRonda([]);
    setPuedeDeshacer(false);
    
    // Mostrar puntos de ronda anterior
    setPuntosRondaAnterior({
      puntos: puntosFinal,
      ronda: rondaActual,
      pie: 'competicion',
      bonus: todosGoles,
      ningunGol: tiros.every(t => t === 0),
      jugador: jugadorActual
    });
    
    // Cambiar al siguiente jugador
    const siguienteIndex = (jugadorActualIndex + 1) % jugadoresValidos.length;
    
    // Si hemos dado la vuelta completa, avanzar ronda
    if (siguienteIndex === 0) {
      if (rondaActual < 7) {
        setAlertaCambio({ tipo: 'ronda', mensaje: `¡RONDA ${rondaActual + 1}!\n${jugadoresValidos[0]} empieza` });
        setTimeout(() => setAlertaCambio(null), 2500);
        setRondaActual(prev => prev + 1);
      } else {
        // Fin de las 7 rondas de la fase actual
        if (faseActual === 'izquierda') {
            // Pasar a derecha
            setFaseActual('derecha');
            setRondaActual(1);
            setAlertaCambio({ tipo: 'pie', mensaje: '¡CAMBIO DE PIE!\n🦶 Ahora pie DERECHO' });
            setTimeout(() => setAlertaCambio(null), 3000);
        } else if (faseActual === 'derecha') {
            // Pasar a dobles
            setFaseActual('doble');
            setRondaActual(1);
            setAlertaCambio({ tipo: 'doble', mensaje: '⚡ ¡RONDAS DOBLES!\nPuntos x2' });
            setTimeout(() => setAlertaCambio(null), 3000);
        } else {
            // Fin de la competición (después de dobles)
            playSound('victoria');
            setShowJuego(false);
            setShowResultadosCompeticion(true);
            setModoCompeticion(false); 
            return;
        }
      }
    } else {
      // Cambio de jugador (sin pitido extra, ya sonó al acabar ronda)
      setAlertaCambio({ tipo: 'jugador', mensaje: `¡Turno de\n${jugadoresValidos[siguienteIndex]}!` });
      setTimeout(() => setAlertaCambio(null), 2000);
    }
    
    setJugadorActualIndex(siguienteIndex);
  };

  const registrarTiroCompeticion = (puntos) => {
    const nuevosTiros = [...tirosRonda, puntos];
    setTirosRonda(nuevosTiros);
    setPuedeDeshacer(true);
    
    if (nuevosTiros.length === 7) {
      guardarRondaCompeticion(nuevosTiros);
    }
  };

  const guardarRonda = async (tiros) => {
    try {
      const res = await axios.post(`${API}/100tiros/partida/${partidaActual}/ronda`, {
        pie: faseActual,
        ronda_numero: rondaActual,
        tiros: tiros
      });
      
      // Sonido de bonus si aplica
      if (res.data.ronda?.bonus) {
        playSound('bonus');
      }
      
      // Guardar puntos de esta ronda para mostrar en la siguiente
      setPuntosRondaAnterior({
        puntos: res.data.puntos_ronda,
        ronda: rondaActual,
        pie: faseActual,
        bonus: res.data.ronda?.bonus || false,
        ningunGol: res.data.ronda?.ningun_gol || false
      });
      
      setPuntosAcumulados(prev => prev + res.data.puntos_ronda);
      setTirosRonda([]);
      setPuedeDeshacer(false);
      
      // Pitido al acabar cada ronda de 7 tiros
      playSound('silbato');
      
      if (rondaActual < 7) {
        setRondaActual(prev => prev + 1);
      } else if (faseActual === 'izquierda') {
        // ¡Cambio de pie!
        setAlertaCambio({ tipo: 'pie', mensaje: '¡CAMBIO DE PIE!\n🦶 Ahora pie DERECHO' });
        setTimeout(() => setAlertaCambio(null), 3000);
        setFaseActual('derecha');
        setRondaActual(1);
      } else {
        // ¡Rondas dobles!
        setAlertaCambio({ tipo: 'doble', mensaje: '⚡ ¡RONDAS DOBLES!\nPuntos x2' });
        setTimeout(() => setAlertaCambio(null), 3000);
        setFaseActual('doble');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const registrarTiroDoble = async (puntos) => {
    try {
      const pie = faseActual === 'doble' ? 'izquierda' : 'derecha';
      await axios.post(`${API}/100tiros/partida/${partidaActual}/tiro-doble`, {
        pie: pie,
        puntos: puntos
      });
      
      setPuntosAcumulados(prev => prev + (puntos * 2));
      
      if (faseActual === 'doble') {
        setFaseActual('doble_derecha');
      } else {
        finalizarPartida();
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const finalizarPartida = async () => {
    try {
      const res = await axios.post(`${API}/100tiros/partida/${partidaActual}/finalizar`);
      
      // Música y efectos según resultado
      if (res.data.es_record) {
        playSound('record');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 8000);
      } else {
        playSound('victoria');
      }
      
      // Guardar estadísticas finales
      setEstadisticasFinales({
        puntuacion_total: res.data.puntuacion_total,
        rango: res.data.rango,
        es_record: res.data.es_record,
        desglose: res.data.desglose || null,
        rondas_detalle: res.data.rondas_detalle || null
      });
      
      setShowJuego(false);
      setShowEstadisticas(true);
      setPartidaActual(null);
      setPuntosRondaAnterior(null);
      setAlertaCambio(null);
      fetchData();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const getRangoColor = (rango) => {
    const colores = {
      'Novato': 'bg-gray-500',
      'Principiante': 'bg-gray-600',
      'Amateur': 'bg-green-600',
      'Promesa': 'bg-green-500',
      'Jugador': 'bg-blue-600',
      'Talento': 'bg-blue-500',
      'Goleador': 'bg-purple-600',
      'Artillero': 'bg-purple-500',
      'Estrella': 'bg-yellow-600',
      'Figura': 'bg-yellow-500',
      'Crack': 'bg-orange-600',
      'Máquina': 'bg-orange-500',
      'Leyenda': 'bg-red-600',
      'Mito': 'bg-red-500',
      'Inmortal': 'bg-pink-600',
      'Dios del Gol ⚽👑': 'bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500'
    };
    return colores[rango] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-mk">
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}
      <Navbar />
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Banner de publicidad */}
        <AnuncioBanner />
        
        <div className="mb-6">
          <h1 className="text-2xl sm:text-4xl font-bold text-gold flex items-center gap-2">
            <Target className="h-8 w-8" /> 100 Tiros
          </h1>
          <p className="text-gray-400 mt-1">El juego de los 100 tiros a portería</p>
        </div>

        {/* Panel del jugador */}
        {perfil ? (
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  {perfil.foto_jugador ? (
                    <img src={`${BACKEND_URL}${perfil.foto_jugador}`} alt={perfil.nombre_jugador} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white">{perfil.nombre_jugador}</h2>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Badge className="bg-blue-600">{CATEGORIAS[perfil.categoria_edad]?.nombre}</Badge>
                    <Badge className={getRangoColor(perfil.rango)}>{perfil.rango}</Badge>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Mejor: {perfil.mejor_puntuacion} pts | Partidas: {perfil.partidas_jugadas}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={mostrarMedidasYJugar} className="btn-primary-mk" size="lg">
                    <Play className="h-5 w-5 mr-2" /> Solo
                  </Button>
                  <Button onClick={() => setShowCompeticion(true)} className="bg-red-600 hover:bg-red-700" size="lg">
                    <Users className="h-5 w-5 mr-2" /> Competición
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardContent className="p-6 text-center">
              <Target className="h-16 w-16 text-gold mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">¡Bienvenido a 100 Tiros!</h2>
              <p className="text-gray-400 mb-4">Crea tu perfil de jugador para empezar a competir</p>
              <Button onClick={() => setShowCrearPerfil(true)} className="btn-primary-mk">
                Crear Perfil
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="ranking" className="w-full">
          <TabsList className={`grid w-full ${user?.role === 'entrenador' ? 'grid-cols-3' : 'grid-cols-2'} bg-gray-900 mb-4`}>
            <TabsTrigger value="ranking">🏆 Ranking</TabsTrigger>
            <TabsTrigger value="historial">📊 Mi Historial</TabsTrigger>
            {user?.role === 'entrenador' && (
              <TabsTrigger value="admin">🛡️ Admin</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="ranking">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <div className="flex flex-col gap-3">
                  <CardTitle className="text-gold">Ranking</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <Select value={porteriaFiltro} onValueChange={setPorteriaFiltro}>
                      <SelectTrigger className="w-36 bg-gray-800 border-gray-700 text-sm">
                        <SelectValue placeholder="Portería" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="todas">🥅 Todas</SelectItem>
                        <SelectItem value="pequena">🥅 Pequeña</SelectItem>
                        <SelectItem value="sala">⚽ Fútbol Sala</SelectItem>
                        <SelectItem value="futbol11">🏟️ Fútbol 11</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
                      <SelectTrigger className="w-32 bg-gray-800 border-gray-700 text-sm">
                        <SelectValue placeholder="Edad" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="todas">Todas</SelectItem>
                        {Object.entries(CATEGORIAS).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {ranking.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No hay puntuaciones aún</p>
                ) : (
                  <div className="space-y-2">
                    {ranking.map((jugador, index) => (
                      <div key={jugador.id} className={`flex items-center gap-3 p-3 rounded-lg ${index < 3 ? 'bg-gray-800' : 'bg-gray-850'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          index === 2 ? 'bg-orange-600 text-white' :
                          'bg-gray-700 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                          {jugador.foto_jugador ? (
                            <img src={`${BACKEND_URL}${jugador.foto_jugador}`} alt={jugador.nombre_jugador} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{jugador.nombre_jugador}</p>
                          <div className="flex gap-2">
                            <Badge className="bg-blue-600 text-xs">{CATEGORIAS[jugador.categoria_edad]?.nombre}</Badge>
                            <Badge className={`${getRangoColor(jugador.rango)} text-xs`}>{jugador.rango}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gold">{jugador.mejor_puntuacion}</p>
                          <p className="text-xs text-gray-400">puntos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="historial">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-gold">Mi Historial de Partidas</CardTitle>
              </CardHeader>
              <CardContent>
                {historial.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No has jugado ninguna partida aún</p>
                ) : (
                  <div className="space-y-2">
                    {historial.map((partida) => (
                      <div key={partida.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                        <div>
                          <p className="text-white font-semibold">{partida.puntuacion_total} puntos</p>
                          <p className="text-gray-400 text-sm">
                            {new Date(partida.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <Badge className={getRangoColor(partida.rango)}>{partida.rango}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pestaña de Administración (solo entrenador) */}
          {user?.role === 'entrenador' && (
            <TabsContent value="admin">
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-gold flex items-center gap-2">
                    <Shield className="h-5 w-5" /> Administrar Partidas
                  </CardTitle>
                  <p className="text-gray-400 text-sm">Edita o elimina partidas incorrectas</p>
                </CardHeader>
                <CardContent>
                  {todasPartidas.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No hay partidas registradas</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {todasPartidas.map((partida) => (
                        <div key={partida.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                          <div className="flex-1">
                            <p className="text-white font-semibold">{partida.nombre_jugador}</p>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gold font-bold">{partida.puntuacion_total} pts</span>
                              <Badge className={`${getRangoColor(partida.rango)} text-xs`}>{partida.rango}</Badge>
                              <span className="text-gray-500">
                                {new Date(partida.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                abrirEditarPartida(partida);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-400 hover:bg-red-900/30"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                eliminarPartidaAdmin(partida.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Dialog Editar Partida (Admin) */}
        <Dialog open={showEditarPartida} onOpenChange={setShowEditarPartida}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-gold">Editar Puntuación</DialogTitle>
            </DialogHeader>
            {partidaEditando && (
              <div className="space-y-4">
                <div className="text-center p-3 bg-gray-800 rounded-lg">
                  <p className="text-white font-semibold">{partidaEditando.nombre_jugador}</p>
                  <p className="text-gray-400 text-sm">
                    {new Date(partidaEditando.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Nueva Puntuación (0-1000)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="1000"
                    value={nuevaPuntuacion}
                    onChange={(e) => setNuevaPuntuacion(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-center text-2xl"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-gray-600"
                    onClick={() => setShowEditarPartida(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 btn-primary-mk"
                    onClick={guardarEdicionPartida}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Crear Perfil */}
        <Dialog open={showCrearPerfil} onOpenChange={setShowCrearPerfil}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold">Crear Perfil de Jugador</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-center">
                <label className="cursor-pointer">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center border-2 border-dashed border-gray-600 hover:border-gold">
                    {fotoPreview ? (
                      <img src={fotoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <input type="file" accept="image/*" onChange={handleFotoChange} className="hidden" />
                </label>
              </div>
              
              <div className="space-y-2">
                <Label>Nombre de Jugador</Label>
                <Input
                  value={nombreJugador}
                  onChange={(e) => setNombreJugador(e.target.value)}
                  placeholder="Tu nombre"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Categoría de Edad</Label>
                <Select value={categoriaEdad} onValueChange={setCategoriaEdad}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Selecciona tu categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {Object.entries(CATEGORIAS).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.nombre} ({val.edad})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={crearPerfil} disabled={!nombreJugador || !categoriaEdad} className="w-full btn-primary-mk">
                Crear Perfil
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Medidas de Portería */}
        <Dialog open={showMedidas} onOpenChange={setShowMedidas}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold text-center text-xl">📐 Elige tu Portería</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Selector de portería */}
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(PORTERIAS).map(([key, porteria]) => (
                  <button
                    key={key}
                    onClick={() => setPorteriaSeleccionada(key)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      porteriaSeleccionada === key 
                        ? 'border-gold bg-gold/20' 
                        : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-2xl mb-1">
                      {key === 'pequena' ? '🥅' : key === 'sala' ? '⚽' : '🏟️'}
                    </div>
                    <div className={`text-xs font-bold ${porteriaSeleccionada === key ? 'text-gold' : 'text-gray-300'}`}>
                      {porteria.nombre}
                    </div>
                  </button>
                ))}
              </div>

              {/* Medidas de la portería seleccionada */}
              <div className="bg-gradient-to-b from-green-900/50 to-green-800/30 rounded-xl p-4 border border-green-600/50">
                <div className="text-center mb-3">
                  <div className="inline-block border-4 border-white rounded bg-green-700/50 px-6 py-3">
                    <div className="text-3xl">🥅</div>
                  </div>
                </div>
                
                <div className="space-y-2 text-center">
                  <div className="bg-gray-800/80 rounded-lg p-2">
                    <p className="text-gold font-bold text-sm">📏 Medidas:</p>
                    <p className="text-white text-lg font-bold">
                      {PORTERIAS[porteriaSeleccionada].ancho} × {PORTERIAS[porteriaSeleccionada].alto}
                    </p>
                  </div>
                  
                  <div className="bg-gray-800/80 rounded-lg p-2">
                    <p className="text-gold font-bold text-sm">📍 Distancia de tiro:</p>
                    <p className="text-white text-lg font-bold">{PORTERIAS[porteriaSeleccionada].distancia}</p>
                  </div>
                  
                  <div className="bg-gray-800/80 rounded-lg p-2">
                    <p className="text-gold font-bold text-sm">🪑 Portero:</p>
                    <p className="text-gray-300 text-sm">Una silla centrada (no se mueve)</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 border-gray-600"
                  onClick={() => setShowMedidas(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 btn-primary-mk"
                  onClick={() => iniciarPartida(modoCompeticion)}
                >
                  <Play className="h-4 w-4 mr-2" /> ¡Empezar!
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Juego */}
        <Dialog open={showJuego} onOpenChange={() => {}}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-lg [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            {/* Alerta de cambio de fase/jugador */}
            {alertaCambio && (
              <div className="absolute inset-0 bg-black/80 z-50 flex items-center justify-center animate-pulse">
                <div className={`text-center p-8 rounded-2xl ${
                  alertaCambio.tipo === 'doble' ? 'bg-purple-900/80 border-4 border-purple-400' : 
                  alertaCambio.tipo === 'jugador' ? 'bg-orange-900/80 border-4 border-orange-400' :
                  alertaCambio.tipo === 'ronda' ? 'bg-yellow-900/80 border-4 border-yellow-400' :
                  'bg-blue-900/80 border-4 border-blue-400'
                }`}>
                  <p className="text-5xl mb-4">
                    {alertaCambio.tipo === 'doble' ? '⚡' : 
                     alertaCambio.tipo === 'jugador' ? '👤' :
                     alertaCambio.tipo === 'ronda' ? '🔄' : '🦶'}
                  </p>
                  <p className="text-3xl font-bold text-white whitespace-pre-line">{alertaCambio.mensaje}</p>
                </div>
              </div>
            )}
            
            {/* MODO COMPETICIÓN - Nombre del jugador y MARCADOR VISIBLE */}
            {modoCompeticion && (
              <>
                {/* Marcador de todos los jugadores - SIEMPRE VISIBLE */}
                <div className="bg-gray-800 rounded-xl p-3 mb-2 border border-gray-600">
                  <p className="text-gray-400 text-xs text-center mb-2">📊 MARCADOR</p>
                  <div className="flex justify-center gap-4">
                    {jugadoresCompeticion.filter(n => n.trim()).map((j, i) => (
                      <div 
                        key={j} 
                        className={`text-center px-4 py-2 rounded-lg ${
                          i === jugadorActualIndex 
                            ? 'bg-orange-600 border-2 border-orange-400' 
                            : 'bg-gray-700'
                        }`}
                      >
                        <p className={`font-bold text-lg ${i === jugadorActualIndex ? 'text-white' : 'text-gray-300'}`}>
                          {j}
                        </p>
                        <p className={`text-2xl font-bold ${
                          puntuacionesCompeticion[j] > 0 ? 'text-gold' : 'text-gray-400'
                        }`}>
                          {puntuacionesCompeticion[j] || 0}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Jugador actual tirando */}
                <div className="bg-gradient-to-r from-orange-900 to-red-900 rounded-xl p-4 mb-2 text-center border-2 border-orange-500">
                  <p className="text-orange-300 text-xs">🏆 TURNO DE:</p>
                  <p className="text-4xl font-bold text-white animate-pulse">
                    {jugadoresCompeticion.filter(n => n.trim())[jugadorActualIndex]}
                  </p>
                  <p className="text-yellow-400 font-bold mt-1">RONDA {rondaActual} / 7</p>
                </div>
              </>
            )}
            
            {/* Indicador GRANDE de pie y ronda (solo modo normal) */}
            {!modoCompeticion && (
              <div className={`text-center py-4 rounded-xl mb-2 ${
                faseActual === 'izquierda' ? 'bg-blue-900/50 border-2 border-blue-500' :
                faseActual === 'derecha' ? 'bg-green-900/50 border-2 border-green-500' :
                'bg-purple-900/50 border-2 border-purple-500'
              }`}>
                <p className="text-5xl mb-2">
                  {(faseActual === 'izquierda' || faseActual === 'derecha') && '🦶'}
                  {(faseActual === 'doble' || faseActual === 'doble_derecha') && '⚡'}
                </p>
                <p className={`text-2xl font-bold ${
                  faseActual === 'izquierda' ? 'text-blue-400' :
                  faseActual === 'derecha' ? 'text-green-400' :
                  'text-purple-400'
                }`}>
                  {faseActual === 'izquierda' && 'PIE IZQUIERDO'}
                  {faseActual === 'derecha' && 'PIE DERECHO'}
                  {faseActual === 'doble' && '¡RONDA DOBLE!'}
                  {faseActual === 'doble_derecha' && '¡RONDA DOBLE!'}
                </p>
                {(faseActual === 'izquierda' || faseActual === 'derecha') && (
                  <p className="text-yellow-400 text-xl font-bold mt-1">RONDA {rondaActual} / 7</p>
                )}
                {(faseActual === 'doble' || faseActual === 'doble_derecha') && (
                  <p className="text-yellow-400 text-lg mt-1">
                    {faseActual === 'doble' ? 'Pie Izquierdo (x2)' : 'Pie Derecho (x2)'}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-4">
              {/* Puntos ronda anterior */}
              {puntosRondaAnterior && (
                <div className={`text-center p-2 rounded-lg ${
                  puntosRondaAnterior.bonus ? 'bg-yellow-900/40 border border-yellow-600' :
                  puntosRondaAnterior.ningunGol ? 'bg-red-900/40 border border-red-600' :
                  'bg-gray-800/50'
                }`}>
                  <p className="text-xs text-gray-400">Ronda anterior ({puntosRondaAnterior.pie === 'izquierda' ? '🦶Izq' : '🦶Der'} #{puntosRondaAnterior.ronda})</p>
                  <p className={`text-lg font-bold ${
                    puntosRondaAnterior.bonus ? 'text-yellow-400' :
                    puntosRondaAnterior.ningunGol ? 'text-red-400' :
                    'text-white'
                  }`}>
                    {puntosRondaAnterior.puntos > 0 ? '+' : ''}{puntosRondaAnterior.puntos} pts
                    {puntosRondaAnterior.bonus && ' ✨ BONUS x2!'}
                    {puntosRondaAnterior.ningunGol && ' 😢 Sin goles'}
                  </p>
                </div>
              )}
              
              {/* Marcador */}
              <div className="text-center bg-gray-800 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Puntos acumulados</p>
                <p className="text-4xl font-bold text-gold">{puntosAcumulados}</p>
              </div>

              {/* Tiros de la ronda actual */}
              {(faseActual === 'izquierda' || faseActual === 'derecha') && (
                <>
                  {/* Marcadores de tiros */}
                  <div className="flex justify-center gap-2 mb-2">
                    {[...Array(7)].map((_, i) => (
                      <div key={i} className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg transition-all ${
                        tirosRonda[i] !== undefined 
                          ? tirosRonda[i] === 5 ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 scale-110' 
                          : tirosRonda[i] === 1 ? 'bg-gradient-to-b from-green-400 to-green-600' 
                          : 'bg-gradient-to-b from-red-400 to-red-600'
                          : 'bg-gray-700 border-2 border-gray-600'
                      }`}>
                        {tirosRonda[i] !== undefined ? (
                          tirosRonda[i] === 0 ? '✗' : tirosRonda[i] === 5 ? '⭐' : '✓'
                        ) : (
                          <span className="text-gray-500">{i + 1}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <p className="text-center text-white text-lg font-semibold">Tiro {tirosRonda.length + 1} de 7</p>

                  {/* Botones de puntuación mejorados */}
                  <div className="grid grid-cols-3 gap-3">
                    <Button 
                      onClick={() => registrarTiro(0)} 
                      className="h-24 bg-gradient-to-b from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 border-2 border-red-400 shadow-lg rounded-xl"
                    >
                      <div className="text-center text-white">
                        <span className="text-3xl block">✗</span>
                        <span className="text-sm font-bold">FALLO</span>
                        <span className="text-xs opacity-75 block">0 pts</span>
                      </div>
                    </Button>
                    <Button 
                      onClick={() => registrarTiro(1)} 
                      className="h-24 bg-gradient-to-b from-green-500 to-green-700 hover:from-green-400 hover:to-green-600 border-2 border-green-400 shadow-lg rounded-xl"
                    >
                      <div className="text-center text-white">
                        <span className="text-3xl block">⚽</span>
                        <span className="text-sm font-bold">GOL</span>
                        <span className="text-xs opacity-75 block">1 pt</span>
                      </div>
                    </Button>
                    <Button 
                      onClick={() => registrarTiro(5)} 
                      className="h-24 bg-gradient-to-b from-yellow-500 to-yellow-700 hover:from-yellow-400 hover:to-yellow-600 border-2 border-yellow-400 shadow-lg rounded-xl"
                    >
                      <div className="text-center text-white">
                        <span className="text-3xl block">⭐</span>
                        <span className="text-sm font-bold">ESPECIAL</span>
                        <span className="text-xs opacity-75 block">5 pts</span>
                      </div>
                    </Button>
                  </div>

                  {/* Botón Deshacer */}
                  {tirosRonda.length > 0 && (
                    <Button 
                      onClick={deshacerUltimoTiro} 
                      variant="outline" 
                      className="w-full border-orange-500 text-orange-400 hover:bg-orange-900/30 font-bold"
                    >
                      <Undo2 className="w-4 h-4 mr-2" />
                      ↩️ Deshacer último tiro
                    </Button>
                  )}

                  {tirosRonda.length === 7 && tirosRonda.every(t => t > 0) && (
                    <div className="text-center p-2 bg-yellow-900/30 rounded-lg border border-yellow-600">
                      <Zap className="w-6 h-6 text-yellow-500 mx-auto" />
                      <p className="text-yellow-500 font-bold">¡BONUS x2!</p>
                    </div>
                  )}

                  {tirosRonda.length === 7 && tirosRonda.every(t => t === 0) && (
                    <div className="text-center p-2 bg-red-900/30 rounded-lg border border-red-600">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                      <p className="text-red-500 font-bold">-1 punto (ningún gol)</p>
                    </div>
                  )}
                </>
              )}

              {/* Ronda doble */}
              {(faseActual === 'doble' || faseActual === 'doble_derecha') && (
                <>
                  <div className="text-center p-4 bg-purple-900/30 rounded-lg border border-purple-600">
                    <Zap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-purple-400 font-bold">¡RONDA DOBLE!</p>
                    <p className="text-sm text-gray-400">Los puntos se multiplican x2</p>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Button onClick={() => registrarTiroDoble(0)} variant="outline" className="h-20 bg-red-900/30 border-red-600 hover:bg-red-900/50">
                      <div className="text-center">
                        <X className="w-8 h-8 mx-auto text-red-500" />
                        <span className="text-sm">Fallo (0pt)</span>
                      </div>
                    </Button>
                    <Button onClick={() => registrarTiroDoble(1)} variant="outline" className="h-20 bg-green-900/30 border-green-600 hover:bg-green-900/50">
                      <div className="text-center">
                        <Check className="w-8 h-8 mx-auto text-green-500" />
                        <span className="text-sm">Gol (2pt)</span>
                      </div>
                    </Button>
                    <Button onClick={() => registrarTiroDoble(5)} variant="outline" className="h-20 bg-yellow-900/30 border-yellow-600 hover:bg-yellow-900/50">
                      <div className="text-center">
                        <Star className="w-8 h-8 mx-auto text-yellow-500" />
                        <span className="text-sm">Especial (10pt)</span>
                      </div>
                    </Button>
                  </div>
                </>
              )}

              {/* Progreso */}
              <div className="text-center text-sm text-gray-400">
                <span className={faseActual === 'izquierda' ? 'text-gold' : ''}>Izquierda</span>
                {' → '}
                <span className={faseActual === 'derecha' ? 'text-gold' : ''}>Derecha</span>
                {' → '}
                <span className={faseActual.startsWith('doble') ? 'text-gold' : ''}>Doble</span>
              </div>

              {/* Botón Abandonar */}
              <Button 
                variant="outline" 
                className="w-full border-red-600 text-red-400 hover:bg-red-900/30 mt-4 font-bold"
                onClick={abandonarPartida}
              >
                <X className="w-4 h-4 mr-2" />
                🚪 Abandonar partida
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Estadísticas Finales */}
        <Dialog open={showEstadisticas} onOpenChange={setShowEstadisticas}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold text-center text-2xl">
                🎯 ¡Partida Completada!
              </DialogTitle>
            </DialogHeader>
            {estadisticasFinales && (
              <div className="space-y-4">
                {/* Puntuación principal */}
                <div className="text-center p-6 bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl border border-gold/30">
                  <p className="text-gray-400 text-sm mb-2">Puntuación Final</p>
                  <p className="text-6xl font-bold text-gold mb-2">{estadisticasFinales.puntuacion_total}</p>
                  <Badge className={`${getRangoColor(estadisticasFinales.rango)} text-lg px-4 py-1`}>
                    {estadisticasFinales.rango}
                  </Badge>
                  {estadisticasFinales.es_record && (
                    <div className="mt-4 p-3 bg-yellow-900/30 rounded-lg border border-yellow-600">
                      <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-1" />
                      <p className="text-yellow-400 font-bold">¡NUEVO RÉCORD PERSONAL!</p>
                    </div>
                  )}
                </div>
                
                {/* Desglose por sección */}
                {estadisticasFinales.desglose && (
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gold font-bold text-center mb-3">📊 Desglose por Sección</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-blue-900/40 p-3 rounded-lg border border-blue-700">
                        <p className="text-blue-400 text-xs">🦶 Izquierda</p>
                        <p className="text-2xl font-bold text-white">{estadisticasFinales.desglose.izquierda || 0}</p>
                      </div>
                      <div className="bg-green-900/40 p-3 rounded-lg border border-green-700">
                        <p className="text-green-400 text-xs">🦶 Derecha</p>
                        <p className="text-2xl font-bold text-white">{estadisticasFinales.desglose.derecha || 0}</p>
                      </div>
                      <div className="bg-purple-900/40 p-3 rounded-lg border border-purple-700">
                        <p className="text-purple-400 text-xs">⚡ Doble</p>
                        <p className="text-2xl font-bold text-white">{estadisticasFinales.desglose.doble || 0}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Desglose por ronda visual */}
                {estadisticasFinales.rondas_detalle && (
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-gold font-bold text-center mb-3">📈 Desglose por Ronda</p>
                    <div className="space-y-3">
                      {/* Pie Izquierdo */}
                      <div>
                        <p className="text-blue-400 text-sm mb-2">🦶 Pie Izquierdo</p>
                        <div className="flex gap-1">
                          {estadisticasFinales.rondas_detalle.izquierda?.map((ronda, i) => (
                            <div key={i} className={`flex-1 p-2 rounded text-center text-xs ${
                              ronda.bonus ? 'bg-yellow-600 text-black' : 
                              ronda.ningun_gol ? 'bg-red-900 text-red-300' : 
                              'bg-gray-700'
                            }`}>
                              <p className="font-bold">{ronda.puntos}</p>
                              <p className="opacity-70">R{i+1}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Pie Derecho */}
                      <div>
                        <p className="text-green-400 text-sm mb-2">🦶 Pie Derecho</p>
                        <div className="flex gap-1">
                          {estadisticasFinales.rondas_detalle.derecha?.map((ronda, i) => (
                            <div key={i} className={`flex-1 p-2 rounded text-center text-xs ${
                              ronda.bonus ? 'bg-yellow-600 text-black' : 
                              ronda.ningun_gol ? 'bg-red-900 text-red-300' : 
                              'bg-gray-700'
                            }`}>
                              <p className="font-bold">{ronda.puntos}</p>
                              <p className="opacity-70">R{i+1}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 justify-center mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-600 rounded"></div> Bonus x2</span>
                      <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-900 rounded"></div> Sin goles</span>
                    </div>
                  </div>
                )}
                
                <Button 
                  className="w-full btn-primary-mk"
                  onClick={() => {
                    setShowEstadisticas(false);
                    setEstadisticasFinales(null);
                  }}
                >
                  Cerrar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Configurar Competición */}
        <Dialog open={showCompeticion} onOpenChange={setShowCompeticion}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold text-center text-xl flex items-center justify-center gap-2">
                <Users className="h-6 w-6" /> Modo Competición
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-400 text-center text-sm">
                Cada jugador tira una ronda por turnos. ¡El que más puntos acumule gana!
              </p>
              
              {/* Lista de jugadores */}
              <div className="space-y-2">
                <Label className="text-gray-300">Jugadores</Label>
                {jugadoresCompeticion.map((nombre, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Jugador ${index + 1}`}
                      value={nombre}
                      onChange={(e) => {
                        const nuevos = [...jugadoresCompeticion];
                        nuevos[index] = e.target.value;
                        setJugadoresCompeticion(nuevos);
                      }}
                      className="bg-gray-800 border-gray-600"
                    />
                    {jugadoresCompeticion.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400"
                        onClick={() => {
                          setJugadoresCompeticion(jugadoresCompeticion.filter((_, i) => i !== index));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {jugadoresCompeticion.length < 6 && (
                  <Button
                    variant="outline"
                    className="w-full border-dashed border-gray-600"
                    onClick={() => setJugadoresCompeticion([...jugadoresCompeticion, ''])}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Añadir jugador
                  </Button>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1 border-gray-600" onClick={() => setShowCompeticion(false)}>
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={jugadoresCompeticion.filter(n => n.trim()).length < 2}
                  onClick={() => {
                    const jugadoresValidos = jugadoresCompeticion.filter(n => n.trim());
                    if (jugadoresValidos.length >= 2) {
                      setModoCompeticion(true);
                      setPuntuacionesCompeticion(Object.fromEntries(jugadoresValidos.map(j => [j, 0])));
                      setRondasCompeticion(Object.fromEntries(jugadoresValidos.map(j => [j, []])));
                      setJugadorActualIndex(0);
                      setShowCompeticion(false);
                      setShowMedidas(true);
                    }
                  }}
                >
                  <Play className="h-4 w-4 mr-2" /> ¡Comenzar!
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Resultados Competición */}
        <Dialog open={showResultadosCompeticion} onOpenChange={setShowResultadosCompeticion}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gold text-center text-2xl">
                🏆 ¡Competición Terminada!
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Ranking final */}
              {Object.entries(puntuacionesCompeticion)
                .sort(([,a], [,b]) => b - a)
                .map(([jugador, puntos], index) => (
                  <div 
                    key={jugador}
                    className={`p-4 rounded-xl ${
                      index === 0 ? 'bg-gradient-to-r from-yellow-600 to-orange-600 border-2 border-yellow-400' :
                      index === 1 ? 'bg-gray-700 border border-gray-500' :
                      'bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}º`}
                        </span>
                        <span className={`text-xl font-bold ${index === 0 ? 'text-white' : 'text-gray-200'}`}>
                          {jugador}
                        </span>
                      </div>
                      <span className={`text-2xl font-bold ${index === 0 ? 'text-white' : 'text-gold'}`}>
                        {puntos} pts
                      </span>
                    </div>
                    {/* Desglose por ronda */}
                    {rondasCompeticion[jugador] && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {rondasCompeticion[jugador].map((r, i) => (
                          <span 
                            key={i} 
                            className={`text-xs px-2 py-1 rounded ${
                              r.bonus ? 'bg-yellow-600 text-black' : 'bg-gray-600'
                            }`}
                          >
                            R{i+1}: {r.puntos}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              }
              
              <Button 
                className="w-full btn-primary-mk"
                onClick={() => {
                  setShowResultadosCompeticion(false);
                  setModoCompeticion(false);
                  setPuntuacionesCompeticion({});
                  setRondasCompeticion({});
                  setRondaActual(1);
                  setJugadorActualIndex(0);
                  setTirosRonda([]);
                }}
              >
                Nueva Competición
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reglas del juego */}
        <Card className="bg-gray-900 border-gray-800 mt-6">
          <CardHeader>
            <CardTitle className="text-gold">📜 Reglas del Juego</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-300 space-y-3 text-sm">
            <div className="space-y-2">
              <p className="font-bold text-white">⚽ Material:</p>
              <p>• 7 balones reglamentarios de fútbol de diferentes marcas e inflados</p>
              <p>• Portero: una silla (no se mueve)</p>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-white">📏 Distancia y portería:</p>
              <p>• Distancia de tiro: 8 metros desde la línea de portería</p>
              <p>• Portería: 1 metro de largo x 1,20 metros de alto</p>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-white">🦶 Orden de tiro:</p>
              <p>• Primero se tira con el pie izquierdo (7 rondas de 7 tiros = 49 tiros)</p>
              <p>• Después con el pie derecho (7 rondas de 7 tiros = 49 tiros)</p>
              <p>• Ronda doble final (1 tiro izquierda + 1 tiro derecha)</p>
              <p>• No hay tiempo límite entre tiros</p>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-white">🎯 Puntuación:</p>
              <p>• Gol normal = 1 punto</p>
              <p>• Caño (entre las patas de la silla) = 5 puntos</p>
              <p>• Escuadra = 5 puntos</p>
              <p className="text-red-400">• No meter ningún gol en una ronda = -1 punto</p>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-white">✨ Bonus:</p>
              <p>• Si metes los 7 goles de una ronda, ¡puntos x2!</p>
              <p>• Los tiros de la ronda doble final valen el doble</p>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-white">🏆 Máximo posible: 1000 puntos</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CienTiros;
